import type { NTXProduct } from "./types";

/**
 * Short display names for schedule cards (Grant: "JB-6x3 instead of NTX-PR-6x3…").
 *
 * Prefers an explicit `shortName` from the backend when present; otherwise derives
 * one from the product name: structure-type abbreviation + size, e.g.
 *   "8x8 Jbox"                      → "JB-8x8"
 *   "6x6 Area Inlet w/ 4x4 Grate"   → "AI-6x6"
 *   "48\" Grate Inlet"              → "GI-48"
 *   "7'x3' JBOX"                    → "JB-7x3"
 * Falls back to the productCode minus its "NTX-PR-" prefix, then the full name.
 */

const TYPE_ABBREVIATIONS: Array<[RegExp, string]> = [
  [/j\s*-?\s*box|junction\s*box/i, "JB"],
  [/area\s*inlet/i, "AI"],
  [/grate\s*inlet/i, "GI"],
  [/curb\s*inlet/i, "CI"],
  [/manhole/i, "MH"],
];

const CODE_PREFIX = "NTX-PR-";

/** Extract a compact size from a name: "7'x3'" → "7x3", "8x8" → "8x8", '48"' → "48". */
const sizeFromName = (name: string): string | undefined => {
  const dims = name.match(/(\d+)\s*['"]?\s*[xX]\s*(\d+)/);
  if (dims) return `${dims[1]}x${dims[2]}`;
  const single = name.match(/(\d+)\s*["']/);
  if (single) return single[1];
  return undefined;
};

type ProductNameFields = Pick<NTXProduct, "productName" | "productCode" | "shortName">;

/** Short label for cards and compact rows. */
export const productDisplayName = (product?: ProductNameFields): string => {
  if (!product) return "";
  if (product.shortName) return product.shortName;

  const name = product.productName || "";
  const abbreviation = TYPE_ABBREVIATIONS.find(([re]) => re.test(name))?.[1];
  const size = sizeFromName(name);
  if (abbreviation && size) return `${abbreviation}-${size}`;

  if (product.productCode?.startsWith(CODE_PREFIX)) {
    return product.productCode.slice(CODE_PREFIX.length);
  }
  return product.productCode || name || "—";
};

/** Full traceable label — for tooltips, dialogs, and drill-ins. */
export const productFullLabel = (product?: ProductNameFields): string => {
  if (!product) return "";
  const parts = [product.productCode, product.productName].filter(Boolean);
  return parts.join(" — ");
};

/**
 * Split a form/box label into a leading size token and the descriptive remainder,
 * for the size-organized day grid (B.3 — Grant: "just the box specifically").
 *   "8x8 JBox"   → { size: "8x8", type: "JBox" }
 *   "48\" Grate" → { size: "48",  type: "Grate" }
 *   "6x6"        → { size: "6x6", type: "" }
 *   "Custom"     → { size: "Custom", type: "" }   (no size token → label unchanged)
 */
export const boxLaneLabel = (label: string): { size: string; type: string } => {
  const s = (label || "").trim();
  const dim = s.match(/(\d+)\s*['"]?\s*[xX×]\s*(\d+)/);
  const size = dim ? `${dim[1]}x${dim[2]}` : (s.match(/\b(\d+)\b/)?.[1] ?? s);
  const type = s
    .replace(/(\d+)\s*['"]?\s*[xX×]\s*(\d+)\s*['"]?/, "")
    .replace(/\b\d+\b\s*["']?/, "")
    .replace(/^[^a-zA-Z0-9]+/, "")
    .trim();
  return { size, type: type.toLowerCase() === size.toLowerCase() ? "" : type };
};

/** Compact QC checklist label, e.g. "QC: Pre·Post", or null when none opted in. */
export const qcLabel = (pre?: boolean, post?: boolean): string | null => {
  if (pre && post) return "QC: Pre·Post";
  if (pre) return "QC: Pre";
  if (post) return "QC: Post";
  return null;
};
